import { useQuery } from '@tanstack/react-query';
import { FlaskConical, KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { encode } from 'uqr';
import { ApiError, getSignInProvider } from '../lib/api';
import { cancelSignIn, confirmAuthenticator, enrolAuthenticator, enrolKey, finishSynthetic, finishWithCode, finishWithKey, type NewAuthenticator,
  type PendingSignIn } from '../lib/second-factor';
import { describeKeyError, webauthnAvailable } from '../lib/webauthn';
import { Button } from '../ui/Button';
import { CopyButton, Logo } from '../ui/misc';
import { AuthFrame } from './SignedOut';

const field = 'h-11 w-full rounded-lg border border-line bg-panel px-3 text-[15px] tracking-wide outline-none focus:border-brand/50';

/** Why a second factor was refused, in words the person can act on. */
function describe(error: unknown) {
  if (!(error instanceof ApiError)) return describeKeyError(error);
  if (error.status === 429) return 'Too many wrong codes. Your authenticator is locked for 15 minutes; use a key or a recovery code meanwhile.';
  if (error.status === 401) return 'That didn’t work. Check the code (or use another factor) and try again. After five tries, sign in again.';
  if (error.status === 409) return 'This sign-in can’t do that. Sign in again.';
  return 'Something went wrong. Try again.';
}

/** A QR code as plain SVG squares: the authenticator app scans the otpauth link it holds. */
export function QrCode({ value, label }: { value: string; label: string }) {
  const { data, size } = encode(value, { border: 2 });
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${size} ${size}`} shapeRendering="crispEdges" className="size-44 rounded-lg bg-white p-1">
      {data.flatMap((row, y) => row.map((dark, x) => dark ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#000" /> : null))}
    </svg>
  );
}

/** Recovery codes, shown once: the person keeps them before going on. */
export function RecoveryCodes({ codes, onDone, doneLabel = 'Continue' }: { codes: string[]; onDone: () => void; doneLabel?: string }) {
  const [saved, setSaved] = useState(false);
  const text = `WITTCO RMM recovery codes (each works once)\n\n${codes.join('\n')}\n`;
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'wittco-rmm-recovery-codes.txt'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div>
      <p className="text-ink-2">If you lose your authenticator or key, each of these signs you in once. They aren’t shown again: save them in your password manager or print them.</p>
      <ol className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg bg-sunken px-4 py-3 font-mono text-[13.5px]" aria-label="Recovery codes">
        {codes.map(code => <li key={code}>{code}</li>)}
      </ol>
      <div className="mt-3 flex flex-wrap gap-2">
        <CopyButton value={codes.join('\n')} label="Copy codes" />
        <Button size="sm" variant="ghost" onClick={download}>Download .txt</Button>
      </div>
      <label className="mt-4 flex items-center gap-2 text-[13.5px]">
        <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} /> I’ve saved these codes
      </label>
      <Button variant="primary" className="mt-4 h-11 w-full justify-center" disabled={!saved} onClick={onDone}>{doneLabel}</Button>
    </div>
  );
}

/**
 * The second step of every Google sign-in (WRMM-134): a key touch, an authenticator code or a recovery code; or, for
 * someone who has none yet, setting one up. Completing it sets the session cookie; `onSignedIn` then loads the console.
 */
export function SecondFactor({ pending, onSignedIn, onCancel, onCodes }: { pending: PendingSignIn; onSignedIn: () => void; onCancel: () => void;
  /** Recovery codes are on screen (true) or done with: the gate keeps this page up meanwhile. */
  onCodes?: (showing: boolean) => void }) {
  const provider = useQuery({ queryKey: ['sign-in-provider'], queryFn: ({ signal }) => getSignInProvider(signal), staleTime: Infinity }).data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<NewAuthenticator | null>(null);
  const [codes, setShownCodes] = useState<string[] | null>(null);
  const setCodes = (shown: string[]) => { onCodes?.(true); setShownCodes(shown); };
  const run = async (work: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await work(); } catch (e) { setError(describe(e)); } finally { setBusy(false); }
  };
  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => { await finishWithCode(code.trim()); onSignedIn(); });
  };
  const confirmSetup = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => setCodes((await confirmAuthenticator(code.trim())).recoveryCodes));
  };

  let title = 'Confirm it’s you', body;
  if (codes) {
    title = 'Save your recovery codes';
    body = <RecoveryCodes codes={codes} onDone={onSignedIn} />;
  } else if (setup) {
    title = 'Set up your authenticator app';
    body = (
      <form onSubmit={confirmSetup}>
        <p className="text-ink-2">Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password and others), then enter the six-digit code it shows.</p>
        <div className="mt-4 flex justify-center"><QrCode value={setup.uri} label="QR code for your authenticator app" /></div>
        <p className="mt-3 text-center text-[12.5px] text-ink-3">Can’t scan? Enter this key instead:</p>
        <p className="mt-1 break-all text-center font-mono text-[13px]" data-testid="authenticator-secret">{setup.secret.match(/.{1,4}/g)!.join(' ')}</p>
        <label className="mt-4 block text-[13px] font-medium" htmlFor="setup-code">Code from the app</label>
        <input id="setup-code" className={`${field} mt-1`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
        <Button variant="primary" type="submit" className="mt-4 h-11 w-full justify-center" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Confirm'}</Button>
      </form>
    );
  } else if (pending.enrol) {
    title = 'Set up a second factor';
    body = (
      <>
        <p className="text-ink-2">WITTCO RMM asks for a second factor at every sign-in. Choose one now; you can add another later in Settings.</p>
        <Button variant="primary" className="mt-5 h-11 w-full justify-center" disabled={busy}
          onClick={() => void run(async () => { setCode(''); setSetup(await enrolAuthenticator()); })}>
          <Smartphone className="size-4" /> Use an authenticator app
        </Button>
        {webauthnAvailable() && (
          <Button className="mt-2 h-11 w-full justify-center" disabled={busy} onClick={() => void run(async () => setCodes((await enrolKey()).recoveryCodes))}>
            <KeyRound className="size-4" /> Use a security key or passkey
          </Button>
        )}
      </>
    );
  } else {
    const hasCode = pending.authenticator || pending.recoveryCodes > 0;
    body = (
      <>
        {pending.keys > 0 && webauthnAvailable() && (
          <Button variant="primary" className="mt-1 h-11 w-full justify-center" disabled={busy} onClick={() => void run(async () => { await finishWithKey(); onSignedIn(); })}>
            <KeyRound className="size-4" />{busy ? 'Waiting for your key…' : 'Use your security key or passkey'}
          </Button>
        )}
        {hasCode && (
          <form onSubmit={submitCode} className="mt-4">
            <label className="block text-[13px] font-medium" htmlFor="second-factor-code">
              {pending.authenticator ? 'Code from your authenticator app, or a recovery code' : 'A recovery code'}</label>
            <input id="second-factor-code" className={`${field} mt-1`} autoComplete="one-time-code" maxLength={40} value={code}
              onChange={e => setCode(e.target.value)} autoFocus={pending.keys === 0} />
            <Button type="submit" className="mt-3 h-11 w-full justify-center" disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Continue'}</Button>
          </form>
        )}
        {!hasCode && !(pending.keys > 0 && webauthnAvailable()) && (
          <p className="text-attention">{pending.keys > 0 ? 'Your second factor is a security key, and this browser can’t use one here. Open the console over HTTPS or on localhost.'
            : 'You have no second factor left. Ask WITTCO to reset it.'}</p>
        )}
      </>
    );
  }
  return (
    <AuthFrame>
      <div className="rounded-2xl border border-line bg-panel/95 p-8 shadow-pop backdrop-blur">
        <Logo className="size-10" />
        <h1 className="mt-6 flex items-center gap-2 text-[22px] font-semibold tracking-tight"><ShieldCheck className="size-5 text-brand" />{title}</h1>
        <div className="mt-3">{body}</div>
        {error && <p role="alert" className="mt-3 text-[13px] text-attention">{error}</p>}
        {provider === 'synthetic' && !codes && (
          <Button variant="ghost" className="mt-3 h-10 w-full justify-center" disabled={busy} onClick={() => void run(async () => { await finishSynthetic(); onSignedIn(); })}>
            <FlaskConical className="size-4 text-attention" /> Continue with test second factor
          </Button>
        )}
        {!codes && <button type="button" className="mt-4 w-full text-center text-[12.5px] text-ink-3 underline-offset-2 hover:underline"
          onClick={() => { void cancelSignIn().catch(() => undefined).finally(onCancel); }}>Start over</button>}
      </div>
    </AuthFrame>
  );
}
